import { Article } from '../types';

export const prepaidMeterAcceptedNoElectricity2026: Article = {
    id: 'prepaid-meter-accepted-no-electricity-2026',
    slug: 'prepaid-meter-token-accepted-no-electricity',
    title: 'Prepaid Meter Token Accepted but No Electricity? Complete 2026 Fix',
    excerpt: 'Your token loaded successfully but the house is still dark? Learn how to separate a grid outage from a tripped relay, CIU fault, overload, or rainy-season meter damage—and the safe next step in Nigeria.',
    category: 'Power Generation',
    date: 'August 29, 2026',
    dateModified: 'August 29, 2026',
    readTime: '9 min read',
    author: 'Power.ng Technical Team',
    trending: true,
    keywords: [
        'prepaid meter token accepted but no electricity',
        'prepaid meter loaded but no light Nigeria',
        'prepaid meter not supplying power',
        'prepaid meter relay off',
        'prepaid meter tripped after rain',
        'prepaid meter no power after outage',
        'meter has units but no electricity',
        'prepaid meter fault Nigeria'
    ],
    metaDescription: 'Token accepted but no light? Diagnose a Nigerian prepaid meter with units but no electricity—grid outage, relay trip, CIU fault, overload, or rain damage—and take the safe next step.',
    quickAnswer: 'If your prepaid meter accepted the token but there is still no electricity, first check whether neighbours are also out. If the outage is only at your property, switch off heavy loads and inspect the main breaker and meter display without opening or touching wet equipment. A relay trip, CIU communication fault, overload, low voltage, tamper alarm, or rain damage may be preventing supply. Do not enter codes from an unverified source or touch a wet meter; document the display and report the fault to your DisCo.',
    faqs: [
        {
            question: 'Why did my prepaid meter accept the token but not restore electricity?',
            answer: 'The token may have updated your credit balance while the supply relay remains off because of a local breaker trip, overload, low voltage, CIU communication problem, tamper alarm, meter fault, or an outage on the feeder. Check the meter display and whether neighbours have supply before buying another token.'
        },
        {
            question: 'What should I do if my prepaid meter has units but there is no light?',
            answer: 'Check whether the whole area is out, then inspect your main breaker from a dry, safe position. If the breaker is on and the meter shows credit but no supply, do not open the meter or keep entering random reset codes. Record the meter message and contact your DisCo for a relay, supply, or meter-fault investigation.'
        },
        {
            question: 'Can rain make a prepaid meter stop supplying electricity?',
            answer: 'Rain itself does not prove that the meter is faulty, but heavy rain, flooding, lightning, wind, earth faults, and voltage events can affect electricity infrastructure or equipment. If the meter, keypad, socket, or surrounding wall is wet or damaged, do not touch it. Keep away and report it as an electrical safety fault.'
        },
        {
            question: 'Should I load another token if the first token was accepted?',
            answer: 'No. Do not buy another token until you confirm the first token was credited and identify the supply problem. Check the balance or receipt, keep the transaction reference, and ask your DisCo or vending provider to investigate if the units are missing.'
        },
        {
            question: 'How do I report a faulty prepaid meter in Nigeria?',
            answer: 'Report the issue first to the Customer Complaints Unit or official customer-care channel of the DisCo serving your address. Give your meter number, account or customer number, exact error message, time of failure, token receipt, and photos taken from a safe distance. Keep the complaint ticket and escalate to NERC if the DisCo does not resolve it.'
        },
        {
            question: 'Will I lose my units if my prepaid meter is replaced?',
            answer: 'NERC says the customer and DisCo representative should jointly record the units on a faulty meter, and the customer should be credited with those units within 48 hours after the replacement meter is installed. Keep a photo of the balance and request the credit in writing.'
        }
    ],
    relatedArticles: [
        'prepaid-meter-token-rejected-error-codes-fix',
        'rainy-season-solar-panel-battery-optimisation-nigeria',
        'electricity-billing-dispute-resolution',
        'how-to-file-nerc-complaint'
    ],
    content: `Your prepaid meter accepted the token, the balance increased, and yet your home is still dark. This is a different problem from a rejected token—and buying another token will usually not fix it.

The fastest way to solve it is to separate a **neighbourhood supply outage** from a **meter or home wiring fault**. This guide shows you what to check safely, what each display or symptom usually means, and when to stop troubleshooting and call your DisCo.

> **Safety first:** Never open a prepaid meter, touch a wet meter or keypad, pull damaged cables, bypass the meter, or enter reset codes from an unverified source. If you smell burning, see sparks, hear crackling, or notice water around electrical equipment, move away and report an electrical emergency.

## Quick answer: is the problem the grid, your meter, or your house?

Use this short check before you buy another token:

| What you observe | Most likely area of the problem | Safest next step |
|---|---|---|
| Neighbours are also without power | Feeder, transformer, or wider grid outage | Check your DisCo's official outage channel and report if it is not listed |
| Your meter shows credit but your entire house is off | Main breaker, relay, low voltage, tamper lockout, or meter fault | Switch off heavy loads; inspect the breaker from a dry, safe position; contact your DisCo if it stays off |
| Only one room or circuit is off | Home circuit breaker, socket, appliance, or internal wiring | Stop using the affected circuit and call a qualified electrician |
| Keypad is blank or will not communicate | CIU power, socket, cable, or meter communication fault | Try a known-good dry wall socket only if safe; do not open the meter |
| The problem began after rain, lightning, flooding, or a voltage event | Water ingress, earth fault, surge, or damaged network equipment | Do not touch wet equipment; keep people away and report a safety fault |
| The display shows tamper, alarm, relay, overload, or a brand-specific fault | Meter protection has disconnected supply or detected a fault | Record the exact message and ask the DisCo for the correct procedure |

## Before you touch anything: the five-minute safety check

### 1. Confirm whether your area has power

Look outside or ask a nearby neighbour whether their lights, security lights, or other appliances are working. If several homes are out, your token did not cause the outage. It may be a feeder, transformer, substation, transmission, or planned-maintenance problem.

Do not keep entering the token while the supply is down. Save the receipt and wait for supply to return, unless your DisCo specifically tells you that a code must be loaded while the meter is powered.

Rainy-season outages can involve more than the national grid. In a recent August 2026 Lagos incident, the Transmission Company of Nigeria attributed the event to simultaneous tripping of two EKEDC feeders caused by overcurrent and an earth fault. That is why a successful token load does not necessarily mean electricity can reach your home. See the [TCN clarification reported by Voice of Nigeria](https://von.gov.ng/tcn-clarifies-power-supply-disruptions-in-lagos-jos/).

### 2. Check the receipt and meter balance

Confirm that:

1. The token receipt says **successful** or **accepted**.
2. The meter balance increased, or the meter displayed an acceptance message.
3. You have not loaded the token on a different meter.
4. You have not already used the token in another attempt.

If the balance is missing, the issue may still be a vending or token-credit problem. Use the [complete prepaid token rejection guide](/articles/prepaid-meter-token-rejected-error-codes-fix) rather than loading another token.

### 3. Look at the main breaker—only if it is dry and safe

From a dry position, check whether the main switch or breaker in your home is down. If it has tripped, switch off heavy appliances first and reset it once according to the instructions on the device.

Do not reset it repeatedly. If it trips again, or if there is a burning smell, heat, smoke, buzzing, exposed wire, or water, leave it off and call a qualified electrician or your DisCo. A repeatedly tripping breaker is a warning, not an inconvenience to work around.

### 4. Check the meter display without opening the box

Take a clear photo or write down:

- The meter balance;
- The exact words, letters, or numbers on the screen;
- Any relay, alarm, tamper, overload, or low-voltage message;
- The time the problem started; and
- Whether power returned briefly before going off again.

The exact meaning of a code varies by meter brand and model. Treat the message as evidence for the DisCo, not as permission to try a random code from a social-media post.

### 5. Stop if the equipment is wet or damaged

Do not touch a wet prepaid meter, CIU, socket, exposed conductor, flooded meter box, or cable. Do not stand in water while trying to restore power. Keep children and neighbours away, take a photo only from a safe distance, and report the location to the DisCo or emergency services as appropriate.

## The most common causes—and the safe fix for each

### Cause 1: The wider feeder is still out

**Clue:** Your neighbours are also without supply, or the DisCo has announced an outage.

**What to do:**

1. Check the DisCo's official website, app, SMS, or social account for a planned outage or fault notice.
2. Ask the DisCo to confirm whether your feeder or transformer is affected.
3. Report the outage if it is not listed.
4. Request a complaint or fault ticket number.
5. Keep the token receipt; do not buy another token just because supply has not returned.

Your meter can hold units while the distribution network remains de-energised. The token is credit for electricity consumed; it is not a repair command for the grid.

### Cause 2: The main breaker or a downstream breaker tripped

**Clue:** Neighbours have electricity, but your property is dark. The meter may still show a normal balance.

**What to do:**

1. Turn off high-load appliances such as air conditioners, pressing irons, water heaters, pumps, cookers, and microwaves.
2. From a dry, safe position, check the main breaker and the relevant circuit breakers.
3. Reset a tripped breaker once.
4. Restore appliances one at a time.
5. If the breaker trips again, isolate the affected circuit and call a qualified electrician.

Do not defeat, wedge, or bypass a breaker. It is designed to disconnect supply when the circuit is unsafe.

### Cause 3: The meter relay is off after an outage or voltage event

The relay is the switching part inside the metering system that connects or disconnects your supply. A sudden outage, low voltage, overload, tamper event, or fault can leave the relay open even when the meter still shows credit.

**Clues include:**

- The meter has units but no appliance works;
- The display shows **relay off**, **disconnected**, **overload**, or a similar message;
- Power returned for a moment and went off again; or
- Your neighbours have power and your breakers look normal.

**What to do:**

1. Switch off heavy appliances and unplug sensitive electronics.
2. Check the meter display for a clear reconnection instruction supplied by your DisCo or meter manufacturer.
3. Follow only the instruction in your official meter manual or DisCo message.
4. If there is no official instruction, or the relay will not close, report a meter/relay fault.

Do not use a generic “magic code” unless your DisCo confirms that exact code for your exact meter model. Incorrect codes can create another token or security problem and may complicate the fault report.

### Cause 4: The Customer Interface Unit (CIU) cannot communicate with the meter

Many prepaid systems use a plug-in keypad or CIU inside the home. The CIU may light up while still failing to communicate with the external meter.

**Clues include:**

- The keypad shows **Error 30**, **E-5**, **connection failed**, or a blank response;
- The token is accepted by the vending platform but cannot be transferred to the meter;
- The keypad freezes or resets; or
- It works in one socket but not another.

**Safe checks:**

1. Unplug the CIU from the wall.
2. Check that the CIU, plug, and wall socket are dry and not visibly damaged.
3. If safe, try the CIU in a known-good wall socket on the same property supply—not an extension cable or a wet socket.
4. Make sure the CIU is receiving power and is close enough to communicate according to the meter manual.
5. If it still cannot connect, stop and report the meter/CIU fault to the DisCo.

Do not remove the meter cover, loosen terminals, change wiring, or connect the CIU to a generator circuit unless the manufacturer or a qualified professional has confirmed that arrangement.

### Cause 5: Overload or an internal wiring fault

**Clue:** The problem appears when several high-power appliances start, or the breaker trips immediately when you reset it.

**What to do:**

1. Turn off every appliance and unplug the non-essential ones.
2. Reset the main breaker once if the installation is dry and there is no sign of damage.
3. If supply returns, reconnect appliances gradually.
4. If the breaker trips again, leave the circuit isolated and call an electrician.

Never reconnect a circuit that smells burnt, sparks, heats up, or trips repeatedly. The prepaid meter may be protecting you from a house-wiring fault.

### Cause 6: Rain, flooding, lightning, or a voltage surge damaged equipment

Nigeria's 2026 seasonal forecast points to a normal-to-late end to the rainy season and normal-to-above-normal rainfall overall, with a longer-than-normal rainy season expected in several states. Check the [NiMet seasonal forecast](https://nimet.gov.ng/publication_detail?id=76) when planning household and equipment protection.

Heavy rain does not automatically mean your meter is defective. However, water, wind, lightning, earth faults, and unstable voltage can affect poles, transformers, feeders, service cables, meters, and household circuits.

**After a storm:**

1. Do not touch a meter box, cable, socket, or appliance that is wet or submerged.
2. Keep people away from fallen wires and damaged poles.
3. Switch off your home's main supply only if the switch is dry, accessible, and safe to reach.
4. Unplug sensitive appliances only from a dry location; do not walk through water to reach them.
5. Photograph the damage from a safe distance.
6. Report the fault as both a **supply problem** and an **electrical safety hazard**.

For practical solar backup protection during the same season, see [Solar Panel & Battery Optimisation in Nigeria's Rainy Season](/articles/rainy-season-solar-panel-battery-optimisation-nigeria).

### Cause 7: A tamper alarm or meter fault has disconnected supply

**Clue:** The display shows **tamper**, **alarm**, **bypass**, **cover open**, **fault**, or another protection message even though the token was accepted.

Do not remove seals, open the meter, or argue with the alarm by entering repeated codes. Take a photo from a safe distance and contact your DisCo. Ask for:

- The exact meaning of the message for your meter model;
- A fault or inspection ticket;
- Confirmation of whether the meter has been remotely disconnected; and
- A written record of your balance before any inspection or replacement.

If the meter was damaged by a storm or another event outside your control, say so clearly in the report and attach dated photos.

## What the display is telling you

The following table is a triage guide, not a universal codebook. Meter brands and firmware can use different messages for similar conditions.

| Display or symptom | What it may indicate | What you should do |
|---|---|---|
| Balance increased, but no supply | Relay open, breaker trip, local wiring fault, or feeder outage | Check neighbours and breakers; report if local |
| **Relay off** or **disconnected** | Meter protection or remote disconnection | Remove heavy loads and contact the DisCo for the approved reconnection process |
| **Overload** | Connected load exceeded the meter or circuit limit | Switch off heavy loads; reset only if safe; call an electrician if it repeats |
| **Error 30**, **E-5**, or connection failure | CIU-to-meter communication issue | Check the dry CIU connection; do not open the meter |
| **Tamper**, **alarm**, or **cover** message | Meter protection or suspected interference | Do not touch seals; request a DisCo inspection |
| Blank display | No meter power, damaged display, battery issue, or severe fault | Do not dismantle it; report a meter fault |
| Burning smell, sparks, smoke, heat, or water | Electrical safety emergency | Move away, keep others away, and report immediately |

For rejected, invalid, or failed tokens—including Error 06, Error 70/75, and duplicate-token messages—use [Power.ng's prepaid meter error-code guide](/articles/prepaid-meter-token-rejected-error-codes-fix). That article covers token acceptance problems; this guide covers the “accepted but no electricity” situation.

## How to report the problem to your DisCo

Your first formal contact should be the Customer Complaints Unit or official customer-care channel of the DisCo serving your address. NERC's [complaint guidance](https://nerc.gov.ng/need-help/services/how-to-file-a-complaint/) identifies the DisCo complaints unit as the first point of call.

Send one clear report containing:

1. Your full name and phone number;
2. Meter number and account/customer number;
3. Your full service address and nearest landmark;
4. DisCo name and feeder/transformer name, if known;
5. Token purchase date, amount, and transaction reference;
6. The exact meter message or display state;
7. The time the supply stopped;
8. Whether neighbours still have power;
9. Photos or video taken from a safe, dry distance; and
10. Any previous complaint or fault ticket number.

Ask the agent to classify the issue correctly. Say: **“The token was accepted and the meter has credit, but supply has not returned. Please check the feeder, service line, relay, CIU communication, and meter fault status.”**

Use the [Power.ng DisCo Directory](/tools/disco-directory) if you need to find the official contact channel for your area.

## Your meter balance and replacement rights

Do not let a replacement visit erase the evidence of your units. Before a technician removes a faulty meter:

- Photograph the balance and meter number;
- Save the token receipt and transaction reference;
- Ask the customer and DisCo representative to record the units together;
- Request a written inspection or replacement note; and
- Confirm when the balance will be transferred to the replacement meter.

NERC's published FAQ says that when a faulty meter is replaced, the customer and DisCo representative should jointly note the units on the old meter, and those units should be credited within 48 hours after the new meter is installed. If that credit does not appear, reopen the complaint with the original ticket number and escalate with your evidence.

NERC also says that a metering-system fault should receive urgent repair or replacement, as soon as reasonably practicable and generally within two working days after the DisCo discovers the fault. Read the current [NERC consumer FAQ](https://nerc.gov.ng/faq/) for the regulator's metering and consumer-protection guidance.

## When and how to escalate to NERC

Escalate when the DisCo:

- Closes the ticket without restoring supply or explaining the fault;
- Refuses to inspect a meter that has credit but no output;
- Does not record or return the units on a replaced meter;
- Tells you to tamper with seals or wiring;
- Demands an unofficial payment to repair a DisCo-owned asset; or
- Fails to respond within its published complaint timeline.

Keep the escalation factual. Attach your receipt, meter photos, display message, dates, ticket numbers, and a short timeline. Start with the [Power.ng guide to reporting a DisCo to NERC](/articles/how-to-file-nerc-complaint), then use NERC's official complaint channels.

## Rainy-season prevention checklist

Before the next heavy storm:

- Keep the CIU and all plug connections off the floor and away from windows or leaking walls.
- Make sure the meter box cover is intact and report cracks or missing covers before water enters.
- Do not store items where they can block access to the meter or main breaker.
- Ask a qualified electrician to check earthing, surge protection, and exposed outdoor wiring.
- Keep your meter number, DisCo contact, and recent token receipt where you can reach them quickly.
- Photograph your current meter balance if severe weather is forecast.

After the storm:

- Check the area before resetting anything.
- Keep heavy appliances off until supply is stable.
- Watch for loose wires, leaning poles, flooded equipment, or unusual buzzing.
- Report safety hazards even if your own electricity has returned.

## The bottom line

If your prepaid meter accepted a token but your home has no electricity, do not assume the token failed and do not buy another one immediately.

1. Check whether the neighbourhood is also out.
2. Confirm that the meter received the credit.
3. Inspect the main breaker only from a dry, safe position.
4. Switch off heavy loads and look for a relay, overload, tamper, or communication message.
5. Never touch wet, damaged, sealed, or sparking equipment.
6. Report the correct fault to your DisCo and keep the ticket number.
7. Photograph and document your balance before any meter replacement.

If the issue is an ordinary token rejection rather than an accepted token with no supply, continue with the [prepaid meter token error-code guide](/articles/prepaid-meter-token-rejected-error-codes-fix). If your concern is that the meter is consuming units unusually quickly, use the [home electricity consumption guide](/articles/how-to-calculate-home-electricity-consumption) to test your appliance load before accusing the meter of over-reading.`
};
